<script setup lang="ts">
import { BALLOON_TEXT } from '@/constants/statesOfAddressSystem'

import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'

import { DeliveryMethods } from '@/api/modules/basket/types'

type Props = {
  selectedAddress: DeliveryDataAddress | null
  deliveryMethod?: DeliveryMethods
  isLoading?: boolean
  clickable?: boolean
}

type Emits = {
  (fn: 'onClick'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const balloonImage = computed(() => {
  if (!props.deliveryMethod) return ''

  const images: Record<DeliveryMethods, string> = {
    [DeliveryMethods.COURIER]: '/address-system/delivery.webp',
    [DeliveryMethods.PICKUP]: '/address-system/pickup.webp',
    [DeliveryMethods.HALL]: '/address-system/pickup.webp',
  }

  return images[props.deliveryMethod]
})

const balloonText = computed(() => {
  // В случае если адреса вообще нет
  if (!props.selectedAddress?.address.address) return BALLOON_TEXT.default

  // в случае если вне зоны доставки
  if (!props.selectedAddress?.details) return BALLOON_TEXT.outsideDelivery

  // В случае если нет адреса
  if (!props.selectedAddress?.address.address?.value) {
    return BALLOON_TEXT.determineAddress
  }

  // Время доставки
  if (props.selectedAddress.details.cooking_time) {
    return `~${props.selectedAddress.details.cooking_time} мин.`
  }

  return ''
})

const showErrorText = computed(() => {
  return (
    !props.selectedAddress?.details ||
    !props.selectedAddress?.address.address?.value
  )
})

const showImage = computed(() => {
  return !showErrorText.value && balloonText.value && balloonImage.value
})

const isAvailableToClick = computed(() => {
  return props.clickable && balloonText.value
})

const onClick = () => {
  if (!isAvailableToClick.value) return

  emit('onClick')
}
</script>

<template>
  <div v-if="isLoading" class="balloon-delivery__text">Ищем ваш адрес..</div>

  <div v-else class="balloon-delivery__content" @click="onClick">
    <nuxt-img
      v-if="showImage"
      class="balloon-delivery__image"
      :src="balloonImage"
      format="webp"
      width="24"
      height="24"
    />

    <div class="balloon-delivery__text">
      {{ balloonText }}
    </div>

    <the-icon
      v-if="isAvailableToClick"
      name="expand-down"
      size="16"
      color="var(--ui-white)"
    />
  </div>
</template>

<style scoped lang="scss">
.balloon-delivery {
  &__text {
    font-size: 12px;
    font-weight: 600;
    line-height: 14px;
  }

  &__content {
    position: relative;
    display: flex;
    gap: 5px;
    align-items: center;

    &::before {
      position: absolute;
      top: -12px;
      right: -16px;
      bottom: -12px;
      left: -16px;
      content: '';
    }
  }

  &__image {
    margin: -4px 0;
  }
}
</style>
